import * as Sentry from "@sentry/react"

import { LOCAL_STORAGE_WELCOME_TOKEN_KEY } from "./localStorageKeys"
import { WelcomeToken } from "./types/local"

// TODO, need to change to using session storage
class WelcomeTokenStorage {
  private key: string
  private cookieDays: number
  private inMemoryData: WelcomeToken | null

  /**
   * @param key         storage key for localStorage & cookies
   * @param cookieDays  number of days until cookie expiration
   */
  constructor() {
    this.key = LOCAL_STORAGE_WELCOME_TOKEN_KEY
    this.cookieDays = 14 // Valid for 14 days
    this.inMemoryData = null
  }

  private _setCookie = (data: WelcomeToken): void => {
    const expires = new Date(
      Date.now() + this.cookieDays * 24 * 60 * 60 * 1_000
    ).toUTCString()

    const serializedData = JSON.stringify(data)

    // Secure flag only works on HTTPS origins
    document.cookie = [
      `${encodeURIComponent(this.key)}=${encodeURIComponent(serializedData)}`,
      `expires=${expires}`,
      "path=/",
      "Secure",
      "SameSite=Strict",
    ].join("; ")
  }

  private _getCookie = (): WelcomeToken | null => {
    const nameEQ = `${encodeURIComponent(this.key)}=`
    const parts = document.cookie.split(";").map((c) => c.trim())

    for (const part of parts) {
      if (part.startsWith(nameEQ)) {
        try {
          const decoded = decodeURIComponent(part.substring(nameEQ.length))
          return JSON.parse(decoded) as WelcomeToken
        } catch {
          // Invalid JSON, return null
          return null
        }
      }
    }
    return null
  }

  /** Expire the cookie immediately */
  private _removeCookie = (): void => {
    document.cookie = [
      `${encodeURIComponent(this.key)}=`,
      "expires=Thu, 01 Jan 1970 00:00:00 GMT",
      "path=/",
      "Secure",
      "SameSite=Strict",
    ].join("; ")
  }

  /**
   * Store the session data in memory, cookies & localStorage
   * @param data the session data containing qrLookupID and token
   */
  public setSessionData = (data: WelcomeToken): void => {
    this.inMemoryData = data

    // 1) write cookie (secure, samesite=strict)
    this._setCookie(data)

    // 2) write localStorage as secondary persistence
    try {
      window.localStorage.setItem(this.key, JSON.stringify(data))
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          welcomeData: data,
        },
      })
    }
  }

  /**
   * Retrieve the session data, checking in-memory → cookies → localStorage
   * Caches the result in memory.
   */
  public getSessionData = (): WelcomeToken | null => {
    // 1) in‐memory
    if (this.inMemoryData) {
      console.log("%cUsing in memory session data", tokenFromLogStyle)
      return this.inMemoryData
    }

    // 2) secure cookie
    const ck = this._getCookie()
    if (ck) {
      console.log("%cUsing cookie session data", tokenFromLogStyle)
      this.inMemoryData = ck
      return ck
    }

    // 3) localStorage
    try {
      const ls = window.localStorage.getItem(this.key)
      if (ls) {
        const parsed = JSON.parse(ls) as WelcomeToken
        console.log("%cUsing localStorage session data", tokenFromLogStyle)
        this.inMemoryData = parsed
        return parsed
      }
    } catch (error) {
      Sentry.captureException(error)
    }

    console.log(
      "%cNo session data in memory, cookie or localStorage",
      tokenFromLogStyle
    )
    return null
  }

  /**
   * Get just the token from session data
   */
  public getToken = (): string | null => {
    const data = this.getSessionData()
    return data?.token ?? null
  }

  /**
   * Get just the qrLookupID from session data
   */
  public getQrLookupID = (): string | null => {
    const data = this.getSessionData()
    return data?.qrLookupID ?? null
  }

  /** Remove the session data from memory, cookies & localStorage */
  public removeToken = (): void => {
    this.inMemoryData = null
    this._removeCookie()

    try {
      window.localStorage.removeItem(this.key)
    } catch (_) {
      // ignore
    }
  }
}

// Sky
const tokenFromLogStyle = ["color: #0EA5E9", "font-weight: bold"].join(";")

const welcomeTokenStorage = new WelcomeTokenStorage()

export default welcomeTokenStorage
